import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'

interface RedditAccount {
  id: string
  user_id: string
  project_id: string
  reddit_username: string
  access_token: string
  refresh_token: string
  expires_at: string
  created_at: string
  updated_at: string
}

interface RedditConnectionStatus {
  isConnected: boolean
  redditAccount: RedditAccount | null
  isLoading: boolean
  error: Error | null
  isTokenExpired: boolean
  isTokenExpiringSoon: boolean
  needsReauth: boolean
  refreshConnection: () => void
  initiateReauth: () => Promise<void>
}

// Type guard to check if the data is a valid RedditAccount
const isValidRedditAccount = (data: any): data is RedditAccount => {
  return data && 
         typeof data === 'object' && 
         typeof data.access_token === 'string' && 
         typeof data.reddit_username === 'string' &&
         typeof data.expires_at === 'string'
}

export function useRedditConnection(): RedditConnectionStatus {
  const { id: projectId } = useParams()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: redditAccount, isLoading, error } = useQuery({
    queryKey: ['reddit-account', projectId],
    queryFn: async (): Promise<RedditAccount | null> => {
      if (!projectId || !user?.id) return null

      const { data, error } = await supabase
        .from('reddit_accounts' as any)
        .select('*')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle()

      if (error) {
        console.error('Error fetching Reddit account:', error)
        return null
      }

      // Validate the returned data
      if (!isValidRedditAccount(data)) {
        return null
      }

      return data
    },
    enabled: !!projectId && !!user?.id,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes (shorter for better token checks)
    refetchInterval: 5 * 60 * 1000 // Refetch every 5 minutes
  })

  // Check if token is expired or expiring soon
  const getTokenStatus = (account: RedditAccount | null) => {
    if (!account?.expires_at) {
      return { isExpired: false, isExpiringSoon: false }
    }

    const expiresAt = new Date(account.expires_at)
    const now = new Date()
    const thirtyMinutesFromNow = new Date(now.getTime() + 30 * 60 * 1000)
    const fifteenMinutesFromNow = new Date(now.getTime() + 15 * 60 * 1000)

    return {
      isExpired: expiresAt <= now,
      isExpiringSoon: expiresAt <= thirtyMinutesFromNow,
      needsRefreshSoon: expiresAt <= fifteenMinutesFromNow
    }
  }

  const tokenStatus = redditAccount ? getTokenStatus(redditAccount) : { isExpired: false, isExpiringSoon: false, needsRefreshSoon: false }

  const refreshConnection = () => {
    queryClient.invalidateQueries({ queryKey: ['reddit-account', projectId] })
    queryClient.invalidateQueries({ queryKey: ['reddit-connection'] })
    queryClient.invalidateQueries({ queryKey: ['reddit-accounts'] })
  }

  const initiateReauth = async (): Promise<void> => {
    if (!projectId) {
      throw new Error('No project ID available')
    }

    // Generate OAuth URL
    const state = crypto.randomUUID()
    const clientId = import.meta.env.VITE_REDDIT_CLIENT_ID
    const redirectUri = `${window.location.origin}/auth/reddit/callback`
    
    if (!clientId || clientId === 'your_reddit_client_id' || clientId === 'your_reddit_client_id_here') {
      throw new Error('Reddit client ID not configured')
    }
    
    const authUrl = `https://www.reddit.com/api/v1/authorize?` +
      `client_id=${clientId}&` +
      `response_type=code&` +
      `state=${state}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `duration=permanent&` +
      `scope=identity,submit,read,mysubreddits`
    
    // Store state and projectId for callback verification
    localStorage.setItem('reddit_oauth_state', state)
    localStorage.setItem('reddit_oauth_project_id', projectId)
    localStorage.setItem('reddit_oauth_return_url', window.location.href)
    localStorage.setItem('reddit_reauth_reason', 'token_expired')
    
    // Redirect to Reddit OAuth
    window.location.href = authUrl
  }

  const isConnected = !!redditAccount
  const needsReauth = tokenStatus.isExpired || (redditAccount && !redditAccount.access_token)

  return {
    isConnected,
    redditAccount,
    isLoading,
    error,
    isTokenExpired: tokenStatus.isExpired,
    isTokenExpiringSoon: tokenStatus.isExpiringSoon,
    needsReauth,
    refreshConnection,
    initiateReauth
  }
} 